import React, { useState } from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { usePreventGameKeydownListening } from '@hooks';

import { useStyles } from './styles';

interface INameBrandingModalProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  onContinue: (name: string) => void;
}

const MAX_CHARACTERS = 100;

const NameBrandingModal = ({
  open,
  onClose,
  onBack,
  onContinue,
}: INameBrandingModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const [name, setName] = useState('');
  const inputRef = usePreventGameKeydownListening<HTMLInputElement>();

  const handleContinue = () => {
    if (name.trim()) {
      onContinue(name.trim());
      setName('');
    }
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleBack = () => {
    setName('');
    onBack();
  };

  return (
    <Modal open={open} onClose={handleClose} sx={classes.modalRoot}>
      <Box sx={classes.nameContainer}>
        <Box sx={classes.nameHeader}>
          <Box sx={classes.nameHeaderLeft}>
            <IconButton onClick={handleBack} sx={classes.backButton} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography sx={classes.nameTitle}>
              {translate('adminToolbox.defineNameTitle', 'Define a name to your new branding')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={classes.closeButton} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={classes.nameContent}>
          <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, MAX_CHARACTERS))}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            placeholder={translate('adminToolbox.typeNamePlaceholder', 'Please type the name here')}
            sx={classes.textField}
            inputProps={{ maxLength: MAX_CHARACTERS }}
            variant="outlined"
          />
          <Typography sx={classes.characterCount}>
            {MAX_CHARACTERS} {translate('adminToolbox.charactersMaxSimple', 'characters max')}
          </Typography>
        </Box>

        <Box sx={classes.nameFooter}>
          <Button variant="outlined" onClick={handleClose} sx={classes.cancelButton}>
            {translate('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={classes.continueButton}
            disabled={!name.trim()}
          >
            {translate('common.continue', 'Continue')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NameBrandingModal;
